/**
 * Footer component.
 * @module components/theme/Footer/Footer
 */

import React from 'react';
import { Segment } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';

const messages = defineMessages({
  copyright: {
    id: 'Copyright',
    defaultMessage: 'Copyright',
  },
});

/**
 * Component to display the footer.
 * @function Footer
 * @param {Object} intl Intl object
 * @returns {string} Markup of the component
 */
const Footer = ({ intl }) => {
  const { siteActions = [] } = useSelector(
    (state) => ({
      siteActions: state.actions?.actions?.site_actions,
    }),
    shallowEqual,
  );

  return (
    <Segment
      role="contentinfo"
      vertical
      padded
      inverted
      color="grey"
      textAlign="center"
      id="footer"
      aria-label="Footer"
      tabIndex="-1"
    >
      <div className="footer-newsletter">
        <div className="inner-newsletter">
          <a
            href="mailto:office@x-net.at?subject=Anmeldung%20Newsletter&body=Hallo%20ich%20möchte%20mich%20mit%20dieser%20Mailadresse%20zu%20eurem%20Newsletter%20anmelden.%20Danke!"
            rel="noopener noreferrer"
            className="button-link"
          >
            Hier gehts zur Newsletter Anmeldung
          </a>
          <a
            href="mailto:office@x-net.at?subject=Anmeldung%20Newsletter&body=Hallo%20ich%20möchte%20mich%20mit%20dieser%20Mailadresse%20zu%20eurem%20Newsletter%20anmelden.%20Danke!"
            className="button"
          >
            Anmelden
          </a>
        </div>
        <div className="outer-newsletter">
          <a
            href="mailto:office@x-net.at?subject=Anmeldung%20Newsletter&body=Hallo%20ich%20<Vorname>%20<Nachname>%20ich%20möchte%20gerne%20euren%20Newsletter%20abonnieren.%20Danke!"
            className="button-mobile"
          >
            Anmelden
          </a>
        </div>
      </div>
      <div className="footer-outer">
        <div className="footer-inner">
          <div className="footer-button">
            <a
              href="#"
              onClick={(event) => {
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
                event.preventDefault();
              }}
            >
              Zurück nach oben
            </a>
          </div>
          <div className="footer">
            <div className="footer-pack-right">
              <div className="footer-column footer-column-start">
                <h3>Warum mit uns</h3>
                <a href="/de/loesungen-produkte">
                  <p>Lösungen & Produkte</p>
                </a>
                <a href="/de/referenzen">
                  <p>Referenzen</p>
                </a>
                <a href="/de/ueber-uns">
                  <p>Über uns</p>
                </a>
                <a href="/de/forschung">
                  <p>Forschung</p>
                </a>
                <a href="/de/blog">
                  <p>Blog</p>
                </a>
              </div>
              <div className="footer-column">
                <h3>Karriere mit uns</h3>
                <a href="/de/karriere-bei-x-net/karriere">
                  <p>Freie Jobs</p>
                </a>
                {/*<p>Bewerbungsprozess</p>*/}
                {/*<p>FAQs</p>*/}
                <a href="/de/ueber-unser-team/ueber-uns#wrapper-geschaeftsfuehrung">
                  <p>Team</p>
                </a>
              </div>
            </div>
            <div className="footer-pack-left">
              <div className="footer-column social-media">
                <h3>Up-to-Date bleiben</h3>
                <div className="footer-up-to-date">
                  <a
                    href="https://www.facebook.com/XNetServicesGmbh/?locale=de_DE"
                    target="_blank"
                    title="Facebook"
                  >
                    <div className="social-media-icons">
                      <svg
                        width="200"
                        zoomAndPan="magnify"
                        viewBox="0 0 150 149.41406"
                        preserveAspectRatio="xMidYMid"
                        version="1.0"
                        id="svg12"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g id="g14">
                          <path
                            d="m 193.35156,12.070312 h 150 V 162 h -150 z m 0,0"
                            clipRule="nonzero"
                            id="path13"
                          />
                        </g>
                        <path
                          d="M 324.49609,12.363281 H 212.16016 c -10.32422,0 -18.8086,8.472657 -18.8086,18.75 V 142.93359 c 0,10.27735 8.48438,18.84375 18.8086,18.84375 h 55.89843 v -51.5625 H 249.60156 V 87.363281 h 18.45703 V 68.386719 c 0,-15.441407 12.84375,-27.605469 28.35547,-27.605469 h 19.10547 v 23.144531 h -9.74609 c -5.16407,0 -9.29688,-0.683593 -9.29688,4.460938 v 18.976562 h 23.33985 l -9.35938,22.851559 h -13.98047 v 51.5625 h 28.01953 c 10.32813,0 18.85547,-8.5664 18.85547,-18.84375 V 31.113281 c 0,-10.277343 -8.52734,-18.75 -18.85547,-18.75 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path14"
                          clipPath="url(#clipPath13)"
                          transform="translate(-193.35156,-12.363256)"
                        />
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://www.instagram.com/xnet_services/"
                    target="_blank"
                    title="Instagram"
                  >
                    <div className="social-media-icons">
                      <svg
                        width="199.47398"
                        viewBox="0 0 149.60549 149.99998"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g
                          clipPath="url(#ee0fdfc34a)"
                          id="g4"
                          transform="translate(-11.281249,-12.68362)"
                        >
                          <path
                            d="M 124.67969,162.68359 H 47.886719 c -20.292969,0 -36.605469,-16.3125 -36.605469,-36.60546 V 49.289062 c 0,-20.292968 16.3125,-36.605468 36.605469,-36.605468 h 76.394531 c 20.28906,0 36.60547,16.3125 36.60547,36.605468 v 76.394528 c 0,20.28907 -16.31641,36.60157 -36.60547,36.60157 z M 47.886719,24.621094 c -13.527344,0 -24.667969,11.140625 -24.667969,24.667968 v 76.394528 c 0,13.52735 11.140625,24.66797 24.667969,24.66797 h 76.394531 c 13.52734,0 24.66797,-11.14062 24.66797,-24.66797 V 49.289062 c 0,-13.527343 -11.14063,-24.667968 -24.66797,-24.667968 z m 0,0"
                            fillOpacity="1"
                            fillRule="nonzero"
                            id="path4"
                          />
                        </g>
                        <path
                          d="m 75.199221,123.34373 c -26.25782,0 -47.34766,-21.08985 -47.34766,-47.34766 0,-26.26172 21.08984,-47.34766 47.34766,-47.34766 26.261709,0 47.347649,21.08594 47.347649,47.34766 0,26.25781 -21.08594,47.34766 -47.347649,47.34766 z m 0,-83.15625 c -19.89454,0 -35.8086,15.91406 -35.8086,35.80859 0,19.89453 15.91406,35.80859 35.8086,35.80859 19.89453,0 35.808589,-15.91406 35.808589,-35.80859 0,-19.89453 -15.914059,-35.80859 -35.808589,-35.80859 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path5"
                        />
                        <path
                          d="m 75.199221,97.48044 c -11.9336,0 -21.48438,-9.54687 -21.48438,-21.48437 0,-11.9375 9.55078,-21.48438 21.48438,-21.48438 11.9375,0 21.48828,9.54688 21.48828,21.48438 0,11.9375 -9.55078,21.48437 -21.48828,21.48437 z m 0,-31.03515 c -5.17188,0 -9.54688,4.3789 -9.54688,9.55078 0,5.17187 4.375,9.54687 9.54688,9.54687 5.17187,0 9.55078,-4.375 9.55078,-9.54687 0,-5.17188 -4.37891,-9.55078 -9.55078,-9.55078 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path6"
                        />
                        <path
                          d="m 126.52734,31.43357 c 0,0.52344 -0.0508,1.03906 -0.15234,1.55078 -0.10157,0.51563 -0.25391,1.01172 -0.45313,1.49609 -0.19922,0.48047 -0.44531,0.94141 -0.73828,1.375 -0.28906,0.4336 -0.61719,0.83594 -0.98828,1.20313 -0.36719,0.37109 -0.76953,0.69922 -1.20703,0.99219 -0.4336,0.28906 -0.89063,0.53515 -1.375,0.73437 -0.48047,0.19922 -0.98047,0.35156 -1.49219,0.45313 -0.51172,0.10156 -1.03125,0.15234 -1.55078,0.15234 -0.52344,0 -1.04297,-0.0508 -1.55469,-0.15234 -0.51172,-0.10157 -1.00781,-0.25391 -1.49219,-0.45313 -0.48046,-0.19922 -0.9414,-0.44531 -1.375,-0.73437 -0.43359,-0.29297 -0.83593,-0.6211 -1.20703,-0.99219 -0.36718,-0.36719 -0.69922,-0.76953 -0.98828,-1.20313 -0.28906,-0.43359 -0.53515,-0.89453 -0.73437,-1.375 -0.20313,-0.48437 -0.35157,-0.98046 -0.45313,-1.49609 -0.10156,-0.51172 -0.15234,-1.02734 -0.15234,-1.55078 0,-0.52344 0.0508,-1.03906 0.15234,-1.55078 0.10156,-0.51563 0.25,-1.01172 0.45313,-1.4961 0.19922,-0.48046 0.44531,-0.9414 0.73437,-1.375 0.28906,-0.43359 0.6211,-0.83593 0.98828,-1.20312 0.3711,-0.37109 0.77344,-0.70313 1.20703,-0.99219 0.4336,-0.28906 0.89454,-0.53515 1.375,-0.73437 0.48438,-0.19922 0.98047,-0.35157 1.49219,-0.45313 0.51172,-0.10156 1.03125,-0.15234 1.55469,-0.15234 0.51953,0 1.03906,0.0508 1.55078,0.15234 0.51172,0.10156 1.01172,0.25391 1.49219,0.45313 0.48437,0.19922 0.9414,0.44531 1.375,0.73437 0.4375,0.28906 0.83984,0.6211 1.20703,0.99219 0.37109,0.36719 0.69922,0.76953 0.98828,1.20312 0.29297,0.4336 0.53906,0.89454 0.73828,1.375 0.19922,0.48438 0.35156,0.98047 0.45313,1.4961 0.10156,0.51172 0.15234,1.02734 0.15234,1.55078 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path7"
                        />
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://at.linkedin.com/company/x-net-services"
                    target="_blank"
                    title="LinkedIn"
                  >
                    <div className="social-media-icons">
                      <svg
                        width="199.99998"
                        zoomAndPan="magnify"
                        viewBox="0 0 149.99999 149.99998"
                        preserveAspectRatio="xMidYMid"
                        version="1.0"
                        id="svg12"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="m 34.968738,54.40233 h 11.09375 c 0.28515,0 0.55469,0.0508 0.8164,0.16015 0.26172,0.10938 0.48829,0.26172 0.69141,0.46094 0.19922,0.19922 0.35156,0.42969 0.46094,0.69141 0.10547,0.25781 0.16015,0.53125 0.16015,0.8125 v 61.02343 c 0,0.28516 -0.0547,0.55469 -0.16015,0.81641 -0.10938,0.26172 -0.26172,0.49219 -0.46094,0.69141 -0.20312,0.19922 -0.42969,0.35156 -0.69141,0.46093 -0.26171,0.10547 -0.53125,0.16016 -0.8164,0.16016 h -11.09375 c -0.28125,0 -0.55469,-0.0547 -0.8125,-0.16016 -0.26172,-0.10937 -0.49219,-0.26171 -0.69141,-0.46093 -0.19922,-0.19922 -0.35156,-0.42969 -0.46094,-0.69141 -0.10937,-0.26172 -0.16406,-0.53125 -0.16406,-0.81641 V 56.52733 c 0,-0.28125 0.0547,-0.55469 0.16406,-0.8125 0.10938,-0.26172 0.26172,-0.49219 0.46094,-0.69141 0.19922,-0.19922 0.42969,-0.35156 0.69141,-0.46094 0.25781,-0.10937 0.53125,-0.16015 0.8125,-0.16015 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path9"
                        />
                        <path
                          d="m 49.453108,39.34373 c -0.0156,-5.23047 -3.76953,-9.02343 -8.9375,-9.02343 -5.16406,0 -9.0625,3.89453 -9.0625,9.0625 0,2.40234 0.87891,4.64062 2.46875,6.29687 1.63672,1.69922 3.89063,2.63672 6.34375,2.63672 2.71875,0 5.14453,-0.95703 6.83203,-2.70703 1.58204,-1.63672 2.41797,-3.84375 2.35547,-6.22656 0,-0.0117 0,-0.0234 0,-0.0391 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path10"
                        />
                        <path
                          d="M 110.22655,58.69139 C 104.69921,53.74998 98.226548,53.0117 94.859358,53.0117 c -5.03515,0 -9.82812,1.34766 -13.87109,3.89063 -1.76563,1.11328 -3.35938,2.42968 -4.78516,3.95703 l -0.28125,-4.46875 c -0.0156,-0.26953 -0.082,-0.52735 -0.19531,-0.77344 -0.11719,-0.24609 -0.27344,-0.46094 -0.47266,-0.64453 -0.19531,-0.1875 -0.42187,-0.32813 -0.67187,-0.42969 -0.25391,-0.0977 -0.51563,-0.14844 -0.78516,-0.14844 h -9.82812 c -0.29688,0 -0.58203,0.0625 -0.85156,0.17969 -0.27344,0.11719 -0.50782,0.28516 -0.71094,0.50391 -0.19922,0.21484 -0.34766,0.46484 -0.44531,0.74219 -0.0977,0.28125 -0.13672,0.5664 -0.11329,0.86328 0.34766,4.65625 0.4961,9.55078 0.4961,16.35547 v 44.51171 c 0,0.28516 0.0547,0.55469 0.16015,0.81641 0.10938,0.26172 0.26172,0.49219 0.46485,0.69141 0.19922,0.19922 0.42969,0.35156 0.6875,0.46093 0.26172,0.10547 0.53125,0.16016 0.8164,0.16016 h 11.09375 c 0.28125,0 0.55469,-0.0547 0.81641,-0.16016 0.25781,-0.10937 0.48828,-0.26171 0.6875,-0.46093 0.19922,-0.19922 0.35547,-0.42969 0.46094,-0.69141 0.10937,-0.26172 0.16406,-0.53125 0.16406,-0.81641 v -36.6875 c 0,-1.72656 0.22656,-3.34375 0.62891,-4.44531 0.0117,-0.0312 0.0234,-0.0664 0.0351,-0.10547 1.5,-4.89453 6.05078,-9.84375 12.84375,-9.84375 4.20313,0 7.21875,1.4375 9.222662,4.39453 1.80859,2.67188 2.76562,6.65235 2.76562,11.50782 v 35.17968 c 0,0.28516 0.0547,0.55469 0.16407,0.81641 0.10547,0.26172 0.26172,0.49219 0.46093,0.69141 0.19922,0.19922 0.42969,0.35156 0.6875,0.46093 0.26172,0.10547 0.53516,0.16016 0.81641,0.16016 H 116.414 c 0.28516,0 0.55469,-0.0547 0.81641,-0.16016 0.25781,-0.10937 0.48828,-0.26171 0.6875,-0.46093 0.20312,-0.19922 0.35547,-0.42969 0.46484,-0.69141 0.10547,-0.26172 0.16016,-0.53125 0.16016,-0.81641 v -36.4375 c 0,-5.1914 -0.78516,-9.79296 -2.33203,-13.67578 -1.39063,-3.49218 -3.40235,-6.43359 -5.98438,-8.74609 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path11"
                        />
                        <g
                          clipPath="url(#e897dafda6)"
                          id="g12"
                          transform="translate(-11.699232,-192.4883)"
                        >
                          <path
                            d="M 142.17187,192.48828 H 31.226562 c -10.765624,0 -19.527343,8.76172 -19.527343,19.52734 v 110.94532 c 0,10.76562 8.761719,19.52734 19.527343,19.52734 H 142.17187 c 10.76563,0 19.52735,-8.76172 19.52735,-19.52734 V 212.01562 c 0,-10.76562 -8.76172,-19.52734 -19.52735,-19.52734 z m 5.81641,20.47266 v 109.05468 c 0,0.44141 -0.043,0.88282 -0.13281,1.31641 -0.0859,0.4375 -0.21485,0.85938 -0.38281,1.26953 -0.17188,0.41016 -0.37891,0.79688 -0.625,1.16797 -0.25,0.36719 -0.52735,0.71094 -0.84375,1.02344 -0.3125,0.3125 -0.65235,0.59375 -1.02344,0.83984 -0.36719,0.2461 -0.75781,0.45703 -1.16797,0.625 -0.41016,0.17188 -0.83203,0.30078 -1.26953,0.38672 -0.4336,0.0859 -0.8711,0.13281 -1.31641,0.13281 H 32.171875 c -0.441406,0 -0.882813,-0.0469 -1.316406,-0.13281 -0.4375,-0.0859 -0.859375,-0.21484 -1.269531,-0.38672 -0.410157,-0.16797 -0.800782,-0.3789 -1.167969,-0.625 -0.367188,-0.24609 -0.710938,-0.52734 -1.023438,-0.83984 -0.316406,-0.3125 -0.59375,-0.65625 -0.839843,-1.02344 -0.25,-0.37109 -0.457032,-0.75781 -0.625,-1.16797 -0.171876,-0.41015 -0.300782,-0.83203 -0.386719,-1.26953 -0.08594,-0.43359 -0.132813,-0.875 -0.132813,-1.31641 V 212.96094 c 0,-0.44532 0.04687,-0.88282 0.132813,-1.32032 0.08594,-0.43359 0.214843,-0.85546 0.386719,-1.26562 0.167968,-0.41016 0.375,-0.80078 0.625,-1.16797 0.246093,-0.37109 0.523437,-0.71094 0.839843,-1.02344 0.3125,-0.3164 0.65625,-0.59765 1.023438,-0.84375 0.367187,-0.24609 0.757812,-0.45312 1.167969,-0.625 0.410156,-0.16797 0.832031,-0.29687 1.269531,-0.38281 0.433593,-0.0898 0.875,-0.13281 1.316406,-0.13281 H 141.22656 c 0.44531,0 0.88281,0.043 1.31641,0.13281 0.4375,0.0859 0.85937,0.21484 1.26953,0.38281 0.41016,0.17188 0.80078,0.37891 1.16797,0.625 0.37109,0.2461 0.71094,0.52735 1.02344,0.84375 0.3164,0.3125 0.59375,0.65235 0.84375,1.02344 0.24609,0.36719 0.45312,0.75781 0.625,1.16797 0.16796,0.41016 0.29687,0.83203 0.38281,1.26562 0.0898,0.4375 0.13281,0.875 0.13281,1.32032 z m 0,0"
                            fillOpacity="1"
                            fillRule="nonzero"
                            id="path12"
                          />
                        </g>
                      </svg>
                    </div>
                  </a>
                </div>
              </div>
              <div className="footer-column footer-column-end adress">
                <h3>Hier findest du uns</h3>
                <div className="find-us">
                  <div className="contact-details">
                    <div className="companies">
                      <p>X-Net Services GmbH</p>
                      <p>X-Net Technologies GmbH</p>
                      <p>X-Net Holding GmbH</p>
                    </div>
                    <div className="adress-details">
                      <p>Spittelwiese 15</p>
                      <p>4020 Linz</p>
                      <p>
                        Tel.:{' '}
                        <a href="tel:+437327731420">
                          +43&nbsp;732&nbsp;77&nbsp;31&nbsp;42 - 0
                        </a>
                      </p>
                      <p>
                        E-Mail:{' '}
                        <a href="mailto:office@x-net.at">office@x-net.at</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-svg">
          <a href="/">
            <svg
              width="358"
              height="91"
              viewBox="0 0 358 91"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_193_46)">
                <path
                  d="M142.88 11.7253C143.764 17.9331 143.824 24.1477 143.811 30.3754C143.77 49.7559 143.797 69.1364 143.797 88.5169C143.797 90.1635 142.972 90.9868 141.321 90.9868C138.557 90.9868 135.8 90.9868 133.037 90.9868C130.855 90.9868 130.801 90.9204 130.788 88.6895C130.788 88.4903 130.788 88.2912 130.788 88.092C130.788 59.642 130.788 31.1921 130.788 2.74878C130.788 2.15123 130.788 1.55368 130.835 0.956133C130.875 0.371863 131.21 0.0531705 131.819 0.0531705C132.12 0.0531705 132.421 0.0332522 132.722 0.0332522C136.436 0.0332522 140.157 0.05981 143.871 0.0266128C147.137 -0.00658432 149.499 1.3545 151.152 4.17625C160.654 20.3565 170.197 36.5235 179.746 52.6773C182.791 57.8361 185.783 63.0215 188.941 68.1139C190.761 71.0486 192.127 74.2156 193.432 77.4025C193.632 77.8872 193.86 78.3652 194.308 78.7769C194.188 77.7544 194.094 76.7253 193.954 75.7028C193.338 71.2544 193.184 66.7728 193.184 62.2912C193.171 42.4592 193.184 22.6339 193.177 2.80189C193.177 -0.491263 193.452 0.05981 195.995 0.0398917C198.758 0.013334 201.515 0.0266128 204.279 0.0398917C206.14 0.0398917 206.18 0.0930072 206.193 1.99852C206.193 2.2973 206.193 2.59607 206.193 2.89485C206.193 31.3448 206.193 59.7947 206.193 88.238C206.193 88.6895 206.207 89.1344 206.193 89.5858C206.146 90.7809 205.999 90.9669 204.754 90.9735C200.84 91 196.918 91.0266 193.003 90.9668C189.851 90.9137 187.543 89.546 185.91 86.7442C176.247 70.1921 166.516 53.6798 156.78 37.1676C154.09 32.6129 151.486 28.0052 148.642 23.5435C146.648 20.4229 145.102 17.0634 143.684 13.6507C143.416 13.0133 143.162 12.3693 142.901 11.7253H142.88Z"
                  fill="#FFD7C8"
                />
                <path
                  d="M222.608 42.8311C222.608 29.4327 222.608 16.0344 222.608 2.63601C222.608 1.09566 223.38 0.323274 224.924 0.318848C242.095 0.318848 259.26 0.318848 276.432 0.318848C277.984 0.318848 278.763 1.08681 278.767 2.62273C278.767 4.06349 278.828 5.50424 278.346 6.9118C277.342 9.84643 275.247 11.5196 272.136 11.7586C271.587 11.7984 271.031 11.7851 270.483 11.7851C259.842 11.7851 249.196 11.7851 238.555 11.7851C237.079 11.7851 236.34 12.531 236.34 14.0226C236.34 21.492 236.34 28.968 236.34 36.4373C236.34 36.8357 236.354 37.2341 236.354 37.6324C236.354 38.3229 236.715 38.6217 237.384 38.6084C237.833 38.6084 238.288 38.6151 238.743 38.6151C250.795 38.6151 262.84 38.6151 274.893 38.6151C277.288 38.6151 277.282 38.6151 277.302 40.9986C277.315 42.5456 277.302 44.0793 276.726 45.5599C275.823 47.8837 274.19 49.3245 271.727 49.849C270.637 50.0814 269.532 50.088 268.428 50.088C258.437 50.088 248.446 50.088 238.455 50.088C237.054 50.088 236.351 50.7652 236.347 52.1197C236.347 60.3857 236.347 68.6585 236.347 76.9246C236.347 77.0773 236.347 77.2233 236.347 77.376C236.374 78.9562 236.441 79.0425 238.087 79.0425C247.523 79.0492 256.965 79.0425 266.401 79.0425C270.115 79.0425 273.829 79.0425 277.543 79.0425C277.944 79.0425 278.346 79.0492 278.747 79.0625C279.457 79.0824 279.872 79.421 279.898 80.158C279.905 80.3572 279.925 80.5563 279.925 80.7555C279.925 83.4976 279.945 86.233 279.925 88.9751C279.912 90.4424 279.724 90.6084 278.286 90.6616C277.984 90.6748 277.683 90.6616 277.382 90.6616C262.071 90.6616 246.76 90.6616 231.448 90.6616C230.391 90.6616 229.341 90.5952 228.31 90.3628C224.736 89.5727 222.675 87.0829 222.642 83.4578C222.608 80.1181 222.635 76.7851 222.635 73.4455C222.635 63.2341 222.635 53.0226 222.635 42.8112L222.608 42.8311Z"
                  fill="#FFD7C8"
                />
                <path
                  d="M329.118 50.9313C329.118 63.3802 329.118 75.8358 329.118 88.2847C329.118 90.6682 329.118 90.6815 326.682 90.6682C324.975 90.6616 323.269 90.7745 321.576 90.4823C317.554 89.7852 315.412 87.3153 315.412 83.252C315.406 60.1933 315.412 37.1345 315.406 14.0758C315.406 11.3603 315.673 11.5064 312.782 11.5064C305.401 11.5064 298.027 11.5064 290.645 11.5064C288.444 11.5064 288.384 11.4466 288.377 9.22903C288.377 6.68613 288.357 4.14987 288.384 1.60697C288.397 0.199407 288.551 0.0732574 289.943 0.0400602C290.244 0.0334208 290.545 0.0400602 290.846 0.0400602C312.381 0.0400602 333.916 0.0400602 355.45 0.0400602C357.993 0.0400602 358 0.0400603 357.987 2.5896C357.987 3.73158 358.06 4.87356 357.793 6.00891C356.969 9.49461 354.5 11.4997 350.913 11.5064C344.435 11.5196 337.964 11.5064 331.486 11.5064C331.085 11.5064 330.683 11.4931 330.282 11.5196C329.305 11.586 329.178 11.7122 329.124 12.6815C329.097 13.1264 329.118 13.5779 329.118 14.0227C329.118 26.3256 329.118 38.6284 329.118 50.9313Z"
                  fill="#FFD7C8"
                />
                <path
                  d="M53.7902 32.8784L29.0834 0.537794C28.8291 0.199183 28.4276 0 27.9993 0H10.0046C9.57635 0 9.17483 0.199183 8.91384 0.537794L0.274516 11.9709C-0.0935417 12.4556 -0.0868497 13.1195 0.274516 13.5975L24.747 45.4469L0.32136 77.4091C-0.0466979 77.8871 -0.0466979 78.5511 0.32136 79.0357L8.9473 90.429C9.2016 90.7676 9.60981 90.9668 10.0381 90.9668H28.0194C28.4477 90.9668 28.8492 90.7676 29.1035 90.4356L53.7969 58.1747L78.4902 90.4356C78.7445 90.7743 79.146 90.9668 79.5743 90.9668H97.5556C97.9839 90.9668 98.3854 90.7676 98.6464 90.429L107.272 79.0357C107.64 78.5511 107.634 77.8871 107.272 77.4091L82.84 45.4535L107.313 13.6042C107.681 13.1261 107.687 12.4556 107.313 11.9775L98.6665 0.537794C98.4122 0.199183 98.004 0 97.5757 0H79.581C79.1527 0 78.7512 0.199183 78.4969 0.537794L53.7902 32.8784Z"
                  fill="#FFD7C8"
                />
              </g>
              <defs>
                <clipPath id="clip0_193_46">
                  <rect width="358" height="91" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </a>
        </div>
        <div className="footer-end">
          <a href="/de/impressum">Impressum</a>
          <a href="/de/datenschutz">Datenschutz</a>
        </div>
      </div>
    </Segment>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Footer.propTypes = {
  /**
   * i18n object
   */
};

export default injectIntl(Footer);
